import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@antimatterhq/ui/components/dialog';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Form, FormikProvider, useFormik } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { useSetAtom } from 'jotai';
import { RequestNewDatasourceSchemaValues } from '@/zod/schemas/dataSource/RequestNewDatasource';
import { FormError, FormInput } from '@/components/form/form';
import { useCreateRequestDatasource } from '@/hooks/content/useCreateRequestDatasource';
import { showRequestNewDatasourceDialogAtom } from '@/atoms/dataSources/dataSourcesAtoms';

export const RequestNewDatasourceDialog = () => {
  const { mutateAsync: createRequestDatasource } = useCreateRequestDatasource();
  const setShowRequestNewDatasourceDialog = useSetAtom(
    showRequestNewDatasourceDialogAtom,
  );

  const formik = useFormik({
    initialValues: { vendor: '', product: '', path: '' },
    validationSchema: toFormikValidationSchema(
      RequestNewDatasourceSchemaValues,
    ),
    validate: toFormikValidate(RequestNewDatasourceSchemaValues),
    onSubmit: (newValues) => {
      createRequestDatasource(
        {
          data: {
            ...newValues,
          },
        },
        {
          toasts: {
            success: 'Request submitted',
            error: 'Error submitting request',
          },
        },
      );
      setShowRequestNewDatasourceDialog(false);
    },
  });

  const { values, handleChange, handleBlur } = formik;

  return (
    <Dialog
      open={true}
      onOpenChange={(isOpen) =>
        !isOpen && setShowRequestNewDatasourceDialog(false)
      }
    >
      <DialogContent>
        <FormikProvider value={formik}>
          <Form>
            <DialogHeader>
              <DialogTitle>Request a new datasource integration</DialogTitle>
            </DialogHeader>
            <div>
              <Spacer size="lg" />

              <Label htmlFor="vendor" id="vendor-label">
                Vendor
              </Label>
              <FormInput
                name="vendor"
                value={values.vendor}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. cloudflare"
                aria-labelledby="vendor-label"
              />
              <FormError name="vendor" />

              <Spacer size="lg" />

              <Label htmlFor="product" id="product-label">
                Product
              </Label>
              <FormInput
                name="product"
                value={values.product}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. WAF access logs"
                aria-labelledby="product-label"
              />
              <FormError name="product" />

              <Spacer size="lg" />

              <Label htmlFor="path" id="path-label">
                Example data path (volume)
              </Label>
              <FormInput
                name="path"
                value={values.path}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. /Volumes/mycatalog/myschema/myvolume/data"
                aria-labelledby="path-label"
              />
              <FormError name="path" />
              <Spacer size="lg" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This data will be analyzed in order to produce a new datasource
                integration.
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Please make sure that the data path is accessible by the DASL
                Service Principal.
              </p>

              <Spacer size="lg" />
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => setShowRequestNewDatasourceDialog(false)}
                variant="default"
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Submit request
              </Button>
            </DialogFooter>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
};
